import React, { useState, useEffect } from "react";
import "./style.css";

// MUI imports
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Typography,
  TableFooter,
  Tooltip,
} from "@mui/material";

import {
  Info as InfoIcon,
  LocalShipping as ShippingIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  CompareArrows as CompareArrowsIcon,
  Inventory2Outlined as InventoryIcon,
} from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import AssignmentIcon from "@mui/icons-material/Assignment";
import WarningIcon from "@mui/icons-material/Warning";

// ---------- helpers ----------
const normalize = (text) =>
  (text ?? "").toString().replace(/\s+/g, " ").trim().toLowerCase();

const clean = (s) =>
  normalize(s)
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const toDash = (v) =>
  v === undefined || v === null || String(v).trim() === "" ? "-" : v;

const pick = (obj, keys) => {
  for (const k of keys) {
    if (
      obj &&
      obj[k] !== undefined &&
      obj[k] !== null &&
      String(obj[k]).trim() !== ""
    ) {
      return obj[k];
    }
  }
  return undefined;
};

const parseMoney = (s) => {
  if (s == null) return null;
  const n = Number(String(s).replace(/[,₹]/g, ""));
  return Number.isFinite(n) ? n : null;
};

const buildStopwords = (poItems, grnItems, invoiceItems) => {
  const freq = {};
  const allTexts = [
    ...poItems.map(
      (x) => x.description || x.description_of_goods || x.item_name || ""
    ),
    ...grnItems.map((x) => x.item_name || x.description || ""),
    ...invoiceItems.map((x) => x.item_description || x.description || ""),
  ];
  allTexts.forEach((txt) => {
    clean(txt)
      .split(" ")
      .filter(Boolean)
      .forEach((w) => (freq[w] = (freq[w] || 0) + 1));
  });
  const total = Math.max(allTexts.length, 1);
  return new Set(Object.keys(freq).filter((w) => freq[w] / total > 0.6));
};

const keywordsFrom = (s, stopwords) =>
  clean(s)
    .split(" ")
    .filter((w) => w.length > 2 && !stopwords.has(w));

const sameByText = (left, right, stopwords) => {
  const A = new Set(keywordsFrom(left, stopwords));
  const B = new Set(keywordsFrom(right, stopwords));
  let common = 0;
  A.forEach((t) => B.has(t) && common++);
  return common >= 2 || (common >= 1 && (A.size <= 2 || B.size <= 2));
};

// Status Chip Component for modal
const ModalStatusChip = ({ status, type }) => {
  const config = {
    matched: {
      color: "#2E7D32",
      bgcolor: "#E8F5E9",
      icon: <CheckCircleIcon style={{ fontSize: 14 }} />,
    },
    partial: {
      color: "#ED6C02",
      bgcolor: "#FFF3E0",
      icon: <WarningIcon style={{ fontSize: 14 }} />,
    },
    rejected: {
      color: "#D32F2F",
      bgcolor: "#FFEBEE",
      icon: <CancelIcon style={{ fontSize: 14 }} />,
    },
    default: { color: "#757575", bgcolor: "#FAFAFA", icon: "?" },
  };

  const { color, bgcolor, icon } = config[type] || config.default;

  return (
    <Chip
      icon={icon}
      label={status}
      size="small"
      sx={{
        fontSize: "10px",
        fontWeight: 600,
        height: "22px",
        backgroundColor: bgcolor,
        color: color,
        border: `1px solid ${color}30`,
        "& .MuiChip-label": {
          px: 0.5,
        },
        "& .MuiChip-icon": {
          color: color,
          fontSize: "14px",
        },
      }}
    />
  );
};

// ---------- Component ----------
const ThreeWayMatchTable = ({
  poData,
  invoiceData,
  grnData,
  open,
  onClose,
  showSummary,
  selectedCard,
}) => {
  const [modalData, setModalData] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const isExternallyOpen = Boolean(open);

  const closeDialog = () => {
    if (typeof onClose === "function") onClose();
    setShowModal(false);
  };

  // Helper functions for status analysis
  // 1. Item-Level Quantity Status
  const getQuantityStatusForItem = (poQty, grnQty, invQty) => {
    poQty = Number(poQty) || 0;
    grnQty = Number(grnQty) || 0;
    invQty = Number(invQty) || 0;

    if (poQty === 0 && grnQty === 0 && invQty === 0) {
      return { status: "No Data", type: "partial" };
    }

    // 🔴 REJECTED: Quantity EXCEEDING (greater than)
    if (grnQty > poQty || invQty > poQty) {
      return { status: "Exceeding", type: "rejected" };
    }

    // 🟡 PARTIAL: Quantity LESS (smaller than)
    if (grnQty < poQty || invQty < poQty) {
      return { status: "Partial", type: "partial" };
    }

    // ✅ MATCHED: Exact match
    if (poQty === grnQty && poQty === invQty) {
      return { status: "Matched", type: "matched" };
    }

    return { status: "No Match", type: "partial" };
  };

  // 2. Item-Level Price Status
  const getPriceStatusForItem = (poPrice, invPrice) => {
    poPrice = Number(poPrice) || 0;
    invPrice = Number(invPrice) || 0;

    if (poPrice === 0 && invPrice === 0) {
      return { status: "No Data", type: "partial" };
    }

    // 🔴 REJECTED: Price INCREASED (greater than)
    if (invPrice > poPrice) {
      return { status: "Increased", type: "rejected" };
    }

    // 🟡 PARTIAL: Price DECREASED (smaller than)
    if (invPrice < poPrice) {
      return { status: "Partial", type: "partial" };
    }

    // ✅ MATCHED: Same price
    if (poPrice === invPrice) {
      return { status: "Matched", type: "matched" };
    }

    return { status: "No Match", type: "partial" };
  };

  // 3. Item-Level Item Status
  const getItemStatusForRow = (row, poItems, grnItems, invItems) => {
    // Check if this specific item exists in all documents

    const existsInPO = poItems.some(
      (item) =>
        (item.part_no || "").includes(row.partNo) ||
        (item.description || "").includes(row.item)
    );

    const existsInGRN = grnItems.some(
      (item) =>
        (item.part_no || "").includes(row.partNo) ||
        (item.item_name || "").includes(row.item)
    );

    const existsInInvoice = invItems.some(
      (item) =>
        (item.part_no || "").includes(row.partNo) ||
        (item.description_of_goods || "").includes(row.item)
    );

    // 🔴 REJECTED: Extra item (not in PO but in GRN/Invoice)
    if (!existsInPO) {
      return { status: "Extra Item", type: "rejected" };
    }

    // 🟡 PARTIAL: Missing from GRN or Invoice
    if (!existsInGRN || !existsInInvoice) {
      return { status: "Missing", type: "partial" };
    }

    // ✅ MATCHED: Exists in all three
    return { status: "Matched", type: "matched" };
  };

  useEffect(() => {
    if (isExternallyOpen || showModal) {
      const poItems = poData?.[0]?.line_items || [];
      const grnItems = grnData?.[0]?.line_items || [];
      const invoiceItems = invoiceData?.[0]?.line_items || [];

      const STOPWORDS = buildStopwords(poItems, grnItems, invoiceItems);

      const merged = poItems.map((poItem) => {
        const partNo = pick(poItem, [
          "part_no",
          "partno",
          "item_code",
          "itemcode",
        ]);
        const itemCode = pick(poItem, [
          "item_code",
          "itemcode",
          "part_no",
          "partno",
        ]);
        const poDesc =
          pick(poItem, ["description_of_goods", "description", "item_name"]) ||
          "";

        const grn =
          grnItems.find(
            (g) =>
              clean(pick(g, ["item_code", "part_no", "partno"]) || "") ===
                clean(partNo || itemCode || "") ||
              clean(pick(g, ["item_code", "part_no", "partno"]) || "") ===
                clean(itemCode || partNo || "")
          ) ||
          grnItems.find((g) =>
            sameByText(poDesc, pick(g, ["item_name"]) || "", STOPWORDS)
          );

        let invoice =
          invoiceItems.find(
            (inv) =>
              clean(pick(inv, ["item_code", "part_no", "partno"]) || "") &&
              (clean(pick(inv, ["item_code", "part_no", "partno"]) || "") ===
                clean(partNo || "") ||
                clean(pick(inv, ["item_code", "part_no", "partno"]) || "") ===
                  clean(itemCode || ""))
          ) ||
          invoiceItems.find((inv) =>
            sameByText(
              poDesc || pick(grn || {}, ["item_name"]) || "",
              pick(inv, ["description", "item_description", "particulars"]) ||
                "",
              STOPWORDS
            )
          );

        if (!invoice) {
          const poHSN = pick(poItem, ["hsn_code", "hsn"]);
          invoice = invoiceItems.find((inv) => {
            const invHSN = pick(inv, ["hsn", "hsn_sac"]);
            const poRate = parseMoney(
              pick(poItem, ["rate", "unit_price", "price"])
            );
            const invRate = parseMoney(
              pick(inv, [
                "unit_rate",
                "rate",
                "unit_price",
                "price",
                "parsed_rate",
              ])
            );
            const rateClose =
              poRate != null &&
              invRate != null &&
              Math.abs(poRate - invRate) < 0.01;
            return (
              (poHSN && invHSN && String(poHSN) === String(invHSN)) || rateClose
            );
          });
        }

        const poQty =
          parseMoney(
            pick(poItem, ["po_qty", "qty.", "qty", "quantity", "po_quantity"])
          ) || 0;

        const grnQty =
          parseMoney(
            pick(grn || {}, [
              "received_q",
              "received_qty",
              "mrn_qty",
              "qty",
              "unt",
              "received_quantity",
              "received_qty.",
            ])
          ) || 0;

        const invQty =
          parseMoney(
            pick(invoice || {}, [
              "order_quantity",
              "quantity",
              "qty",
              "invoice_qty",
              "parsed_quantity",
              "unt",
            ])
          ) || 0;

        const unitPricePO =
          parseMoney(pick(poItem, ["rate", "unit_price", "price"])) || 0;
        const unitPriceInvoice =
          parseMoney(
            pick(invoice || {}, [
              "unit_rate",
              "unit_price",
              "rate",
              "price",
              "parsed_rate",
            ])
          ) || 0;

        let totalPricePO = parseMoney(
          pick(poItem, ["amount", "total", "line_total"])
        );
        if (totalPricePO === undefined || totalPricePO === null) {
          totalPricePO = (poQty * unitPricePO).toFixed(2);
        }

        let totalPriceInvoice = parseMoney(
          pick(invoice || {}, ["line_total", "amount", "total"])
        );
        if (totalPriceInvoice === undefined || totalPriceInvoice === null) {
          totalPriceInvoice = (invQty * unitPriceInvoice).toFixed(2);
        }

        // Calculate variances - now considering all three quantities
        const poVsGrnQtyVariance = grnQty - poQty;
        const grnVsInvQtyVariance = grnQty - invQty;
        const priceVariance = totalPricePO - totalPriceInvoice;

        // Determine variance types
        const poVsGrnQtyVarianceType =
          grnQty > poQty ? "GRN > PO" : grnQty < poQty ? "GRN < PO" : "Equal";

        const grnVsInvQtyVarianceType =
          grnQty > invQty
            ? "GRN > Invoice"
            : grnQty < invQty
            ? "GRN < Invoice"
            : "Equal";

        const priceVarianceType =
          totalPricePO > totalPriceInvoice
            ? "PO Total > Invoice Total"
            : totalPricePO < totalPriceInvoice
            ? "PO Total < Invoice Total"
            : "Equal";

        // Get statuses for this item
        const quantityStatus = getQuantityStatusForItem(poQty, grnQty, invQty);
        const priceStatus = getPriceStatusForItem(
          unitPricePO,
          unitPriceInvoice,
          totalPricePO,
          totalPriceInvoice
        );
        const itemStatus = getItemStatusForRow(
          {
            partNo: partNo || "",
            item: poDesc || "",
          },
          poItems,
          grnItems,
          invoiceItems
        );

        return {
          item: poDesc || "-",
          partNo: toDash(partNo),
          itemCode: toDash(itemCode),
          poQty: poQty,
          grnQty: grnQty,
          invoiceQty: invQty,
          unitPricePO: unitPricePO,
          unitPriceInvoice: unitPriceInvoice,
          totalPricePO: totalPricePO,
          totalPriceInvoice: totalPriceInvoice,
          poVsGrnQtyVariance: poVsGrnQtyVariance,
          grnVsInvQtyVariance: grnVsInvQtyVariance,
          priceVariance: priceVariance,
          poVsGrnQtyVarianceType: poVsGrnQtyVarianceType,
          grnVsInvQtyVarianceType: grnVsInvQtyVarianceType,
          priceVarianceType: priceVarianceType,
          quantityStatus: quantityStatus,
          priceStatus: priceStatus,
          itemStatus: itemStatus,
        };
      });

      setModalData(merged);
    }
  }, [open, poData, grnData, invoiceData]);

  const formatNumber = (v) => {
    const n = parseMoney(v);
    if (n === 0) return "0.00";
    if (n == null) return "-";
    return n.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatVariance = (variance, type = "number") => {
    if (variance === 0) return "0";
    if (!variance) return "-";

    const absVariance = Math.abs(variance);
    const formattedValue =
      type === "number"
        ? absVariance.toLocaleString("en-IN", {
            maximumFractionDigits: 0,
          })
        : Math.round(absVariance).toString();

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {variance > 0 ? (
          <ArrowUpwardIcon sx={{ color: "#D84315", fontSize: 16, mr: 0.5 }} />
        ) : variance < 0 ? (
          <ArrowDownwardIcon sx={{ color: "#2E7D32", fontSize: 16, mr: 0.5 }} />
        ) : null}
        {formattedValue}
      </Box>
    );
  };

  const getVarianceTooltip = (row, type) => {
    if (type === "poVsGrnQty") {
      if (row.poVsGrnQtyVariance > 0) {
        return `GRN quantity (${row.grnQty}) is greater than PO quantity (${row.poQty})`;
      } else if (row.poVsGrnQtyVariance < 0) {
        return `GRN quantity (${row.grnQty}) is less than PO quantity (${row.poQty})`;
      } else {
        return "GRN and PO quantities match";
      }
    } else if (type === "grnVsInvQty") {
      if (row.grnVsInvQtyVariance > 0) {
        return `GRN quantity (${row.grnQty}) is greater than Invoice quantity (${row.invoiceQty})`;
      } else if (row.grnVsInvQtyVariance < 0) {
        return `GRN quantity (${row.grnQty}) is less than Invoice quantity (${row.invoiceQty})`;
      } else {
        return "GRN and Invoice quantities match";
      }
    } else {
      if (row.priceVariance > 0) {
        return `PO total price (${formatNumber(
          row.totalPricePO
        )}) is greater than Invoice total price (${formatNumber(
          row.totalPriceInvoice
        )})`;
      } else if (row.priceVariance < 0) {
        return `PO total price (${formatNumber(
          row.totalPricePO
        )}) is less than Invoice total price (${formatNumber(
          row.totalPriceInvoice
        )})`;
      } else {
        return "PO and Invoice total prices match";
      }
    }
  };

  const rawAddress = (() => {
    const addr = poData?.[0]?.shipping_address;

    if (!addr) return "No shipping address provided";

    if (typeof addr === "string") return addr;

    if (typeof addr === "object" && addr.address) return addr.address;

    return "No shipping address provided";
  })();

  const addressLines = rawAddress.split("\n");

  const handleViewItems = () => {
    const poItems = poData?.[0]?.line_items || [];
    const grnItems = grnData?.[0]?.line_items || [];
    const invoiceItems = invoiceData?.[0]?.line_items || [];

    const STOPWORDS = buildStopwords(poItems, grnItems, invoiceItems);

    const merged = poItems.map((poItem) => {
      const partNo = pick(poItem, [
        "part_no",
        "partno",
        "item_code",
        "itemcode",
      ]);
      const itemCode = pick(poItem, [
        "item_code",
        "itemcode",
        "part_no",
        "partno",
      ]);
      const poDesc =
        pick(poItem, ["description_of_goods", "description", "item_name"]) ||
        "";

      const grn =
        grnItems.find(
          (g) =>
            clean(pick(g, ["item_code", "part_no", "partno"]) || "") ===
              clean(partNo || itemCode || "") ||
            clean(pick(g, ["item_code", "part_no", "partno"]) || "") ===
              clean(itemCode || partNo || "")
        ) ||
        grnItems.find((g) =>
          sameByText(poDesc, pick(g, ["item_name"]) || "", STOPWORDS)
        );

      let invoice =
        invoiceItems.find(
          (inv) =>
            clean(pick(inv, ["item_code", "part_no", "partno"]) || "") &&
            (clean(pick(inv, ["item_code", "part_no", "partno"]) || "") ===
              clean(partNo || "") ||
              clean(pick(inv, ["item_code", "part_no", "partno"]) || "") ===
                clean(itemCode || ""))
        ) ||
        invoiceItems.find((inv) =>
          sameByText(
            poDesc || pick(grn || {}, ["item_name"]) || "",
            pick(inv, ["description", "item_description", "particulars"]) || "",
            STOPWORDS
          )
        );

      if (!invoice) {
        const poHSN = pick(poItem, ["hsn_code", "hsn"]);
        invoice = invoiceItems.find((inv) => {
          const invHSN = pick(inv, ["hsn", "hsn_sac"]);
          const poRate = parseMoney(
            pick(poItem, ["rate", "unit_price", "price"])
          );
          const invRate = parseMoney(
            pick(inv, [
              "unit_rate",
              "rate",
              "unit_price",
              "price",
              "parsed_rate",
            ])
          );
          const rateClose =
            poRate != null &&
            invRate != null &&
            Math.abs(poRate - invRate) < 0.01;
          return (
            (poHSN && invHSN && String(poHSN) === String(invHSN)) || rateClose
          );
        });
      }

      const poQty =
        parseMoney(
          pick(poItem, ["po_qty", "qty.", "qty", "quantity", "po_quantity"])
        ) || 0;

      const grnQty =
        parseMoney(
          pick(grn || {}, [
            "received_q",
            "received_qty",
            "mrn_qty",
            "qty",
            "unt",
            "received_quantity",
            "received_qty.",
          ])
        ) || 0;

      const invQty =
        parseMoney(
          pick(invoice || {}, [
            "order_quantity",
            "quantity",
            "qty",
            "invoice_qty",
            "parsed_quantity",
            "unt",
          ])
        ) || 0;

      const unitPricePO =
        parseMoney(pick(poItem, ["rate", "unit_price", "price"])) || 0;
      const unitPriceInvoice =
        parseMoney(
          pick(invoice || {}, [
            "unit_rate",
            "unit_price",
            "rate",
            "price",
            "parsed_rate",
          ])
        ) || 0;

      let totalPricePO = parseMoney(
        pick(poItem, ["amount", "total", "line_total"])
      );
      if (totalPricePO === undefined || totalPricePO === null) {
        totalPricePO = (poQty * unitPricePO).toFixed(2);
      }

      let totalPriceInvoice = parseMoney(
        pick(invoice || {}, ["line_total", "amount", "total"])
      );
      if (totalPriceInvoice === undefined || totalPriceInvoice === null) {
        totalPriceInvoice = (invQty * unitPriceInvoice).toFixed(2);
      }

      // Calculate variances - now considering all three quantities
      const poVsGrnQtyVariance = grnQty - poQty;
      const grnVsInvQtyVariance = grnQty - invQty;
      const priceVariance = totalPricePO - totalPriceInvoice;

      // Determine variance types
      const poVsGrnQtyVarianceType =
        grnQty > poQty ? "GRN > PO" : grnQty < poQty ? "GRN < PO" : "Equal";

      const grnVsInvQtyVarianceType =
        grnQty > invQty
          ? "GRN > Invoice"
          : grnQty < invQty
          ? "GRN < Invoice"
          : "Equal";

      const priceVarianceType =
        totalPricePO > totalPriceInvoice
          ? "PO Total > Invoice Total"
          : totalPricePO < totalPriceInvoice
          ? "PO Total < Invoice Total"
          : "Equal";

      // Get statuses for this item
      const quantityStatus = getQuantityStatusForItem(poQty, grnQty, invQty);
      const priceStatus = getPriceStatusForItem(
        unitPricePO,
        unitPriceInvoice,
        totalPricePO,
        totalPriceInvoice
      );
      const itemStatus = getItemStatusForRow(
        {
          partNo: partNo || "",
          item: poDesc || "",
        },
        poItems,
        grnItems,
        invoiceItems
      );

      return {
        item: poDesc || "-",
        partNo: toDash(partNo),
        itemCode: toDash(itemCode),
        poQty: poQty,
        grnQty: grnQty,
        invoiceQty: invQty,
        unitPricePO: unitPricePO,
        unitPriceInvoice: unitPriceInvoice,
        totalPricePO: totalPricePO,
        totalPriceInvoice: totalPriceInvoice,
        poVsGrnQtyVariance: poVsGrnQtyVariance,
        grnVsInvQtyVariance: grnVsInvQtyVariance,
        priceVariance: priceVariance,
        poVsGrnQtyVarianceType: poVsGrnQtyVarianceType,
        grnVsInvQtyVarianceType: grnVsInvQtyVarianceType,
        priceVarianceType: priceVarianceType,
        quantityStatus: quantityStatus,
        priceStatus: priceStatus,
        itemStatus: itemStatus,
      };
    });

    setModalData(merged);
    setShowModal(true);
  };

  const renderRow = (label, poValue, invoiceValue, grnValue) => (
    <TableRow>
      <TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>{label}</TableCell>
      <TableCell sx={{ fontSize: "14px" }}>{toDash(poValue)}</TableCell>
      <TableCell sx={{ fontSize: "14px" }}>{toDash(invoiceValue)}</TableCell>
      <TableCell sx={{ fontSize: "14px" }}>{toDash(grnValue)}</TableCell>
    </TableRow>
  );

  // Inline styles
  const headerCellStyle = {
    fontWeight: 700,
    fontSize: "12px",
    backgroundColor: "#e8eaf6",
    padding: "8px",
    borderRight: "1px solid #e0e0e0",
  };

  const cellStyle = {
    fontSize: "12px",
    padding: "8px",
    borderRight: "1px solid #f0f0f0",
  };

  return (
    <div>
      {showSummary && (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ borderRadius: 2, overflow: "hidden" }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700 } }}>
                <TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>
                  Key
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>
                  PO
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>
                  Invoice
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>
                  GRN
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {renderRow(
                "Purchase Order No",
                poData?.[0]?.purchase_order_no || poData?.[0]?.order_number,
                invoiceData?.[0]?.purchase_order_number,
                "-"
              )}
              {renderRow(
                "Order No.",
                "-",
                invoiceData?.[0]?.invoice_number,
                grnData?.[0]?.bill_no
              )}
              {renderRow(
                "Vendor Name",
                poData?.[0]?.vendor,
                "-",
                grnData?.[0]?.vendor_name
              )}

              {renderRow(
                "Bill Date",
                poData?.[0]?.order_date,
                invoiceData?.[0]?.invoice_date,
                grnData?.[0]?.bill_date
              )}

              {renderRow(
                "Total Amount",
                poData?.[0]?.total_amount
                  ? Number(
                      String(poData[0].total_amount).replace(/,/g, "")
                    ).toLocaleString("en-IN")
                  : "-",
                invoiceData?.[0]?.invoice_amount
                  ? Number(
                      String(invoiceData[0].invoice_amount).replace(/,/g, "")
                    ).toLocaleString("en-IN")
                  : "-"
              )}

              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>
                  Items
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Inventory2OutlinedIcon />}
                    onClick={handleViewItems}
                    sx={{ fontWeight: 600, fontSize: "12px" }}
                  >
                    View Items
                  </Button>
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Inventory2OutlinedIcon />}
                    onClick={handleViewItems}
                    sx={{ fontWeight: 600, fontSize: "12px" }}
                  >
                    View Items
                  </Button>
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Inventory2OutlinedIcon />}
                    onClick={handleViewItems}
                    sx={{ fontWeight: 600, fontSize: "12px" }}
                  >
                    View Items
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {/* Modal UI */}
      <Dialog
        open={Boolean(open) || showModal}
        onClose={closeDialog}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          style: {
            borderRadius: "8px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#1976d2",
            color: "white",
            padding: "12px 24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <AssignmentIcon style={{ fontSize: 28 }} />
            <Typography variant="h4" style={{ fontWeight: 600 }}>
              Line Item Matching Details
            </Typography>
          </div>
        </DialogTitle>

        <DialogContent
          style={{ padding: 0, overflow: "hidden", overflowX: "auto" }}
        >
          <TableContainer
            component={Paper}
            style={{
              maxHeight: 500, // Scrollable height
              borderRadius: 0,
              border: "none",
              position: "relative", // Important for sticky footer
              overflow: "auto",
            }}
          >
            <Table
              sx={{
                minWidth: 2000, // Increased width for new columns
                // tableLayout: "fixed",
                borderRight: "1px solid #edeff3ff",
              }}
              stickyHeader
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "13px",
                      color: "#334155",
                      position: "sticky",
                      left: 0,
                      zIndex: 3,
                      width: 200,
                      minWidth: 200,
                      backgroundColor: "#f8fafc",
                    }}
                    style={headerCellStyle}
                  >
                    Item Description
                  </TableCell>
                  <TableCell style={headerCellStyle}>Part No</TableCell>
                  <TableCell style={headerCellStyle}>Item Code</TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    PO Qty
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    MRN Qty
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    Invoice Qty
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    Unit Price (PO)
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    Unit Price (Invoice)
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    Total Price (PO)
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    Total Price (Invoice)
                  </TableCell>

                  <TableCell align="center" style={headerCellStyle}>
                    TAX %
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CompareArrowsIcon
                        style={{ fontSize: 16, marginRight: "4px" }}
                      />
                      Qty Variance
                      <Tooltip
                        title="PO vs GRN Quantity Variance"
                        arrow
                        slotProps={{ tooltip: { sx: { fontSize: "12px" } } }}
                      >
                        <InfoIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                      </Tooltip>
                    </div>
                  </TableCell>

                  <TableCell align="center" style={headerCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <TrendingUpIcon
                        style={{ fontSize: 16, marginRight: "4px" }}
                      />
                      Price Variance
                      <Tooltip
                        title="Unit Price Variance"
                        arrow
                        slotProps={{ tooltip: { sx: { fontSize: "12px" } } }}
                      >
                        <InfoIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                      </Tooltip>
                    </div>
                  </TableCell>

                  {/* New Status Columns */}
                  {/* <TableCell align="center" style={headerCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <InventoryIcon
                        style={{ fontSize: 16, marginRight: "4px" }}
                      />
                      Item Status
                      <Tooltip
                        title="Item matching status (PO vs GRN vs Invoice)"
                        arrow
                        slotProps={{ tooltip: { sx: { fontSize: "12px" } } }}
                      >
                        <InfoIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                      </Tooltip>
                    </div>
                  </TableCell> */}

                  <TableCell align="center" style={headerCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <TrendingUpIcon
                        style={{ fontSize: 16, marginRight: "4px" }}
                      />
                      Qty Status
                      <Tooltip
                        title="Quantity matching status"
                        arrow
                        slotProps={{ tooltip: { sx: { fontSize: "12px" } } }}
                      >
                        <InfoIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                      </Tooltip>
                    </div>
                  </TableCell>

                  <TableCell align="center" style={headerCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <MoneyIcon style={{ fontSize: 16, marginRight: "4px" }} />
                      Price Status
                      <Tooltip
                        title="Price matching status"
                        arrow
                        slotProps={{ tooltip: { sx: { fontSize: "12px" } } }}
                      >
                        <InfoIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                      </Tooltip>
                    </div>
                  </TableCell>

                  <TableCell
                    align="center"
                    style={headerCellStyle}
                    sx={{
                      fontWeight: 700,
                      fontSize: "13px",
                      color: "#334155",
                      position: "sticky",
                      right: 100,
                      zIndex: 3,
                      width: 100,
                      minWidth: 100,
                      backgroundColor: "#f8fafc",
                    }}
                  >
                    Qty Match
                  </TableCell>
                  <TableCell
                    align="center"
                    style={headerCellStyle}
                    sx={{
                      fontWeight: 700,
                      fontSize: "13px",
                      color: "#334155",
                      position: "sticky",
                      right: 0,
                      zIndex: 4,
                      width: 100,
                      minWidth: 100,
                      backgroundColor: "#f8fafc",
                    }}
                  >
                    Price Match
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {modalData.map((row, idx) => (
                  <TableRow
                    key={idx}
                    sx={{
                      "& td": {
                        py: 1.8,
                        px: 1.5,
                        borderBottom: "1px solid #f1f5f9",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                    style={{
                      backgroundColor:
                        idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "white",
                      ":hover": { backgroundColor: "rgba(25, 118, 210, 0.04)" },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontSize: "14px",
                        color: "#475569",
                        fontWeight: 600,
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                        minWidth: 100,
                        backgroundColor: "#f8fafc",
                      }}
                      style={cellStyle}
                    >
                      {row.item || "-"}
                    </TableCell>
                    <TableCell style={cellStyle}>{row.partNo || "-"}</TableCell>
                    <TableCell style={cellStyle}>
                      {row.itemCode || "-"}
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        label={row.poQty}
                        style={{
                          fontSize: "11px",
                          backgroundColor: "#e3f2fd",
                          minWidth: "60px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        label={row.grnQty}
                        style={{
                          fontSize: "11px",
                          backgroundColor: "#f3e5f5",
                          minWidth: "60px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        label={row.invoiceQty}
                        style={{
                          fontSize: "11px",
                          backgroundColor: "#e8f5e9",
                          minWidth: "60px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<CurrencyRupeeIcon color="primary" />}
                        label={formatNumber(row.unitPricePO)}
                        style={{
                          fontSize: "11px",
                          borderColor: "#bbdefb",
                          color: "rgba(0, 0, 0, 0.87)",
                          minWidth: "70px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<CurrencyRupeeIcon color="primary" />}
                        label={formatNumber(row.unitPriceInvoice)}
                        style={{
                          fontSize: "11px",
                          borderColor: "#d1c4e9",
                          color: "rgba(0, 0, 0, 0.87)",
                          minWidth: "70px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<CurrencyRupeeIcon color="primary" />}
                        label={formatNumber(row.totalPricePO)}
                        style={{
                          fontSize: "11px",
                          borderColor: "#d1c4e9",
                          color: "rgba(0, 0, 0, 0.87)",
                          minWidth: "70px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<CurrencyRupeeIcon color="primary" />}
                        label={formatNumber(row.totalPriceInvoice)}
                        style={{
                          fontSize: "11px",
                          borderColor: "#d1c4e9",
                          color: "rgba(0, 0, 0, 0.87)",
                          minWidth: "70px",
                        }}
                      />
                    </TableCell>

                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          poData?.[0]?.line_items?.[0]?.igst_rate
                            ? Math.floor(
                                Number(poData[0].line_items[0].igst_rate)
                              )
                            : invoiceData?.[0]?.line_items?.[0]?.gst_rate
                            ? Math.floor(
                                Number(invoiceData[0].line_items[0].gst_rate)
                              )
                            : "-"
                        }
                        style={{
                          fontSize: "11px",
                          borderColor: "#d1c4e9",
                          color: "rgba(0, 0, 0, 0.87)",
                          minWidth: "70px",
                        }}
                      />
                    </TableCell>

                    <TableCell align="center" style={cellStyle}>
                      <Tooltip
                        title={getVarianceTooltip(row, "poVsGrnQty")}
                        slotProps={{ tooltip: { sx: { fontSize: "10px" } } }}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <Chip
                            size="small"
                            label={formatVariance(row.poVsGrnQtyVariance)}
                            variant="outlined"
                            style={{
                              fontSize: "11px",
                              fontWeight: 600,
                              borderColor:
                                row.poVsGrnQtyVariance === 0
                                  ? "#2E7D32"
                                  : "#D84315",
                              color:
                                row.poVsGrnQtyVariance === 0
                                  ? "#2E7D32"
                                  : "#D84315",
                              minWidth: "70px",
                            }}
                          />
                        </div>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center" style={cellStyle}>
                      <Tooltip
                        title={getVarianceTooltip(row, "price")}
                        slotProps={{ tooltip: { sx: { fontSize: "10px" } } }}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <Chip
                            size="small"
                            label={row.priceVariance}
                            variant="outlined"
                            style={{
                              fontSize: "11px",
                              fontWeight: 600,
                              borderColor:
                                row.priceVariance === 0 ? "#2E7D32" : "#D84315",
                              color:
                                row.priceVariance === 0 ? "#2E7D32" : "#D84315",
                              minWidth: "70px",
                            }}
                          />
                        </div>
                      </Tooltip>
                    </TableCell>

                    {/* New Status Cells
                    <TableCell align="center" style={cellStyle}>
                      <Tooltip
                        title={`Item matching status`}
                        slotProps={{ tooltip: { sx: { fontSize: "10px" } } }}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <ModalStatusChip
                            status={row.itemStatus.status}
                            type={row.itemStatus.type}
                          />
                        </div>
                      </Tooltip>
                    </TableCell> */}

                    <TableCell align="center" style={cellStyle}>
                      <Tooltip
                        title={`Quantity status: PO: ${row.poQty}, GRN: ${row.grnQty}, Invoice: ${row.invoiceQty}`}
                        slotProps={{ tooltip: { sx: { fontSize: "10px" } } }}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <ModalStatusChip
                            status={row.quantityStatus.status}
                            type={row.quantityStatus.type}
                          />
                        </div>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center" style={cellStyle}>
                      <Tooltip
                        title={`Price status: PO: ${row.unitPricePO}, Invoice: ${row.unitPriceInvoice}`}
                        slotProps={{ tooltip: { sx: { fontSize: "10px" } } }}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <ModalStatusChip
                            status={row.priceStatus.status}
                            type={row.priceStatus.type}
                          />
                        </div>
                      </Tooltip>
                    </TableCell>

                    <TableCell
                      align="center"
                      style={cellStyle}
                      sx={{
                        fontSize: "14px",
                        color: "#475569",
                        fontWeight: 600,
                        position: "sticky",
                        right: 100,
                        zIndex: 2,
                        minWidth: 135,
                        backgroundColor: "#f8fafc",
                      }}
                    >
                      {row.poQty === row.grnQty &&
                      row.grnQty === row.invoiceQty ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Match"
                          size="small"
                          style={{
                            fontSize: "11px",
                            fontWeight: 600,
                            borderColor: "#2E7D32",

                            color: "#2E7D32",
                            "& .MuiChip-icon": { color: "#2E7D32" },
                          }}
                        />
                      ) : (
                        <Chip
                          icon={<CancelIcon />}
                          label="Mismatch"
                          size="small"
                          style={{
                            fontSize: "11px",
                            fontWeight: 600,
                            borderColor: "#D84315",

                            color: "#D84315",
                            "& .MuiChip-icon": { color: "#D84315" },
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell
                      align="center"
                      style={cellStyle}
                      sx={{
                        fontSize: "14px",
                        color: "#475569",
                        fontWeight: 600,
                        position: "sticky",
                        right: 0,
                        zIndex: 2,
                        minWidth: 100,
                        backgroundColor: "#f8fafc",
                      }}
                    >
                      {row.totalPricePO === row.totalPriceInvoice ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Match"
                          size="small"
                          style={{
                            fontSize: "11px",
                            fontWeight: 600,
                            borderColor: "#2E7D32",
                            color: "#2E7D32",
                            "& .MuiChip-icon": { color: "#2E7D32" },
                          }}
                        />
                      ) : (
                        <Chip
                          icon={<CancelIcon />}
                          label="Mismatch"
                          size="small"
                          style={{
                            fontSize: "11px",
                            fontWeight: 600,
                            borderColor: "#D84315",
                            color: "#D84315",
                            "& .MuiChip-icon": { color: "#D84315" },
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}

                {modalData.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={19} // Updated colSpan from 16 to 19 for new columns
                      align="center"
                      style={{ padding: "32px 16px" }}
                    >
                      <Typography variant="body2" color="textSecondary">
                        No items to display.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

              {/* ✅ Sticky Footer */}
              <TableFooter
                sx={{
                  position: "sticky",
                  bottom: 0,
                  left: 0,
                  zIndex: 3, // ⬆️ increase so it stays above other elements
                  backgroundColor: "#ffffff",
                  boxShadow: "0 -3px 8px rgba(0,0,0,0.1)",
                }}
              >
                {/* Shipping Address Row */}
                <TableRow>
                  <TableCell
                    colSpan={19} // Updated colSpan from 16 to 19
                    sx={{
                      padding: "10px", // ⬆️ increased height
                      borderTop: "2px solid #e0e0e0",
                      backgroundColor: "#f0f7ff",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start" }}>
                      <ShippingIcon
                        style={{
                          color: "#1976d2",
                          marginRight: "8px",
                          marginTop: "4px",
                        }}
                      />
                      <div>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 600, color: "#1976d2", mb: "4px" }}
                        >
                          Shipping Address
                        </Typography>

                        <Typography
                          variant="body1"
                          sx={{ fontSize: "15px", fontWeight: 700 }}
                        >
                          {addressLines[0]}
                        </Typography>

                        {addressLines.slice(1).map((line, idx) => (
                          <Typography
                            key={idx}
                            variant="body2"
                            sx={{ fontSize: "15px", fontWeight: 400 }}
                          >
                            {line}
                          </Typography>
                        ))}
                      </div>
                    </div>
                  </TableCell>
                </TableRow>

                {/* Grand Total Row */}
                <TableRow>
                  <TableCell
                    colSpan={8}
                    sx={{
                      fontSize: "18px",
                      fontWeight: 700,
                      padding: "24px", // ⬆️ taller
                      color: "green",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    Grand Total
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontSize: "18px",
                      fontWeight: 700,
                      padding: "24px",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                    >
                      <CurrencyRupeeIcon fontSize="large" />
                      {poData?.[0]?.total_amount
                        ? formatNumber(poData[0].total_amount)
                        : "-"}
                    </div>
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontSize: "18px",
                      fontWeight: 700,
                      padding: "10px",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                    >
                      <CurrencyRupeeIcon fontSize="large" />
                      {invoiceData?.[0]?.invoice_amount
                        ? formatNumber(invoiceData[0].invoice_amount)
                        : "-"}
                    </div>
                  </TableCell>

                  <TableCell colSpan={8} sx={{ backgroundColor: "#f5f5f5" }} />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </DialogContent>

        <div
          style={{
            padding: "12px 24px",
            display: "flex",
            justifyContent: "flex-end",
            backgroundColor: "#f5f5f5",
          }}
        >
          <Button
            variant="outlined"
            onClick={closeDialog}
            style={{
              borderRadius: "4px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
            }}
          >
            Close
          </Button>
        </div>
      </Dialog>
    </div>
  );
};

export default ThreeWayMatchTable;
